import React, { createContext, useContext, useState, useEffect } from 'react';
import { createMercadoPagoPaymentLink, redirectToMercadoPago } from '../utils/mercadopago';
import { generatePaymentId } from '../utils/paymentId';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartNote, setCartNote] = useState('');
  const [isAddedModalOpen, setIsAddedModalOpen] = useState(false);
  const [lastAddedProduct, setLastAddedProduct] = useState(null);

  // Cargar carrito desde localStorage al iniciar
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    const savedNote = localStorage.getItem('cartNote');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
    if (savedNote) {
      setCartNote(savedNote);
    }
  }, []);

  // Guardar carrito en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Guardar nota en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('cartNote', cartNote);
  }, [cartNote]);

  // Agregar producto al carrito
  const addToCart = (product) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      
      if (existingItem) {
        // Si ya existe, incrementar cantidad
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Si no existe, agregarlo con cantidad 1
        return [...prevItems, { ...product, quantity: 1 }];
      }
    });
    
    // Guardar el producto agregado y abrir modal
    setLastAddedProduct(product);
    setIsAddedModalOpen(true);
  };

  // Remover producto del carrito
  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  // Actualizar cantidad de un producto
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  // Incrementar cantidad
  const incrementQuantity = (productId) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  // Decrementar cantidad
  const decrementQuantity = (productId) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity: Math.max(1, item.quantity - 1) }
          : item
      )
    );
  };

  // Limpiar carrito
  const clearCart = () => {
    setCartItems([]);
    setCartNote('');
  };

  // Calcular total de items
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  // Calcular total de precio
  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.precio * item.quantity), 0);
  };

  // Abrir/cerrar carrito
  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  const openCart = () => {
    setIsCartOpen(true);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  // Abrir/cerrar modal de producto agregado
  const openAddedModal = () => {
    setIsAddedModalOpen(true);
  };

  const closeAddedModal = () => {
    setIsAddedModalOpen(false);
    setLastAddedProduct(null);
  };

  // Generar mensaje de WhatsApp
  const generateWhatsAppMessage = () => {
    const storeName = process.env.REACT_APP_STORE_NAME || 'Tu Tienda';
    let message = `🛒 *Hola ${storeName}* te quiero hacer un nuevo pedido: \n\n`;
    message += `📋 *Productos:*\n`;
    
    cartItems.forEach((item, index) => {
      message += `\n${index + 1}. *${item.descripcion}*\n`;
      message += `   Cantidad: ${item.quantity}\n`;
      message += `   Precio unitario: $${item.precio.toLocaleString()}\n`;
      message += `   Subtotal: $${(item.precio * item.quantity).toLocaleString()}\n`;
    });
    
    message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    message += `💰 *Total: $${getTotalPrice().toLocaleString()}*\n`;
    message += `📦 *Cantidad de items: ${getTotalItems()}*\n`;
    
    if (cartNote.trim()) {
      message += `\n📝 *Nota adicional:*\n${cartNote}`;
    }
    
    //message += `\n\n¡Gracias por tu pedido! 🙏`;
    
    return message;
  };

  // Enviar pedido por WhatsApp
  const sendToWhatsApp = () => {
    const phoneNumber = process.env.REACT_APP_WHATSAPP_NUMBER || '5491112345678';
    const message = generateWhatsAppMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  // Pagar con MercadoPago
  const payWithMercadoPago = async () => {
    if (cartItems.length === 0) {
      alert('El carrito está vacío');
      return;
    }

    // Obtener el UUID del negocio desde variables de entorno
    // Puede ser REACT_APP_UUID_COBRO o usar REACT_APP_COMERCIO como fallback
    const uuid = process.env.REACT_APP_UUID_COBRO || process.env.REACT_APP_COMERCIO;
    
    if (!uuid) {
      alert('Error: No se ha configurado el UUID del negocio. Por favor, configura REACT_APP_UUID_COBRO en el archivo .env');
      return;
    }

    try {
      // Generar un ID único para la compra
      const paymentId = generatePaymentId();
      
      // Crear el link de pago
      const { payment_link } = await createMercadoPagoPaymentLink(cartItems, uuid, paymentId);
      
      // Redirigir al usuario al link de pago
      redirectToMercadoPago(payment_link);
    } catch (error) {
      alert(`Error al procesar el pago: ${error.message || 'Error desconocido'}`);
    }
  };

  const value = {
    cartItems,
    isCartOpen,
    cartNote,
    setCartNote,
    addToCart,
    removeFromCart,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    toggleCart,
    openCart,
    closeCart,
    sendToWhatsApp,
    payWithMercadoPago,
    isAddedModalOpen,
    lastAddedProduct,
    openAddedModal,
    closeAddedModal,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

